import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CompanyTreeRowComponent } from 'src/app/app-common/components/company-tree-row/company-tree-row.component';
import { CompanyTreeSearchService } from 'src/app/app-common/services/company-tree-search.service';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import {
  CompanyNode,
  CompanyTreeRow,
} from 'src/app/app-common/types/company-tree';
import { ComponentsModule } from 'src/app/components/components.module';

@Component({
  selector: 'company-search-results',
  templateUrl: './company-search-results.component.html',
  styleUrl: './company-search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ComponentsModule,
    CompanyTreeRowComponent,
    MatProgressSpinnerModule,
  ],
  host: {
    class: 'company-search-results',
  },
})
export class CompanySearchResultsComponent {
  private readonly searchService = inject(CompanyTreeSearchService);
  private readonly treeService = inject(CompanyTreeService);

  readonly selectCompany = output<string>();

  protected readonly errorMessage = this.searchService.errorMessage;

  protected readonly rows = computed(() => {
    const results = this.searchService.results();
    return results.map((node, index) =>
      this.toRow(node, index, results.length),
    );
  });

  /**
   * The panel's own centered spinner still covers the first page; this one only
   * ever appears under results already on screen.
   */
  protected readonly loadingMore = computed(
    () => this.searchService.loading() && this.rows().length > 0,
  );

  /**
   * Never under the first-page spinner and never beside the error box: both
   * were regressions once, and both leave the query settled in a state the
   * copy would misdescribe.
   */
  protected readonly showEmptyState = computed(
    () =>
      !!this.searchService.query() &&
      !this.searchService.loading() &&
      !this.errorMessage() &&
      this.rows().length === 0,
  );

  /**
   * Empty only in show-all mode, where the search covered the whole hierarchy
   * and there is no narrower scope to name — and when the root's name has not
   * been loaded, rather than rendering a sentence with a gap in it.
   */
  protected readonly searchedCompanyName = computed(() =>
    this.treeService.showAllCompanies()
      ? ''
      : (this.treeService.displayRootCompany()?.name ?? ''),
  );

  protected onWidenSearch(): void {
    void this.treeService.setShowAllCompanies(true);
  }

  protected onSelect(id: string): void {
    const node = this.searchService
      .results()
      .find((result) => result.id === id);
    void this.treeService.select(id, 'search', node);
    this.selectCompany.emit(id);
  }

  protected onScrollEnd(): void {
    this.searchService.load();
  }

  protected onRetry(): void {
    this.searchService.load();
  }

  private toRow(
    node: CompanyNode,
    index: number,
    setSize: number,
  ): CompanyTreeRow {
    const row: CompanyTreeRow = {
      id: node.id,
      name: node.name,
      address: node.address,
      level: 1,
      posInSet: index + 1,
      setSize,
      variant: 'search',
      expandable: false,
      expanded: false,
      selected: false,
      containsSelected: false,
      loading: false,
      loadingMore: false,
      errorMessage: null,
      hasMore: false,
    };

    if (node.displayCount !== undefined) {
      row.displayCount = node.displayCount;
    }

    if (node.parentName) {
      row.parentName = node.parentName;
    }

    return row;
  }
}
